import React, { useState } from 'react';
import { LayoutGrid, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { SiteCard } from './SiteCard';
import { EmptyState } from '@/components/ui/empty-state';
import { Paginator } from '@/components/ui/paginator';
import { useSites } from '../../hooks/useSites';
import { useSiteSubscriptions } from '../../hooks/useSiteSubscriptions';
import type { SiteSubscription } from '../../hooks/useSiteSubscription';
import { useAppCache } from '@/contexts/AppCacheContext';
import { useAuth } from '../../auth/auth0-provider';

const isActiveSub = (sub: SiteSubscription | undefined): boolean => {
  if (!sub) return false;
  // `paid` is computed server-side as `stripe_subscription_id IS NOT NULL
  // AND status IN ('active','trialing')`. Trust it as the single source of
  // truth — status alone is insufficient because the schema historically
  // defaulted subscription_status to 'active' for rows that never had a
  // Stripe subscription, making free-tier sites look active.
  return sub.paid === true;
};

export const SiteList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAppCache();
  const { isAuthenticated, isSdkReady } = useAuth();
  const canCreate = hasPermission('create:sites');

  const [page, setPage] = useState(1);
  const { data, isLoading, error: queryError } = useSites({ page });
  const sites = data?.items ?? [];
  const pagination = data?.pagination;
  const siteIds = sites.map((s) => s.id);
  const { data: subscriptions } = useSiteSubscriptions(siteIds);
  // While auth is initializing, the query is `enabled: false` and TanStack
  // returns `isLoading: false, data: undefined` — without this, the empty
  // state would flash before the first fetch kicks off.
  const loading = isLoading || !isAuthenticated || !isSdkReady;
  const error = queryError instanceof Error
    ? queryError.message
    : queryError
      ? String(queryError)
      : null;
  const handleDeleted = () => {
    void queryClient.invalidateQueries({ queryKey: ['sites'] });
  };

  if (error) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Couldn't load sites"
        description={error}
        action={{ label: 'Try again', onClick: () => window.location.reload(), variant: 'tertiary' }}
      />
    );
  }

  if (!loading && sites.length === 0) {
    return (
      <EmptyState
        icon={LayoutGrid}
        title="No sites yet"
        description="Create your first site to get started."
        action={
          canCreate
            ? {
                label: 'Create new site',
                onClick: () => navigate('/templates'),
              }
            : undefined
        }
      />
    );
  }

  if (loading) {
    return (
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1"
        aria-busy="true"
        aria-live="polite"
      >
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="flex flex-col rounded-2xl overflow-hidden animate-pulse"
            style={{ animationDelay: `${i * 120}ms` }}
          >
            <div className="aspect-video bg-foreground/5" />
            <div className="px-4 py-3 bg-foreground flex items-center">
              <div className="h-5 w-1/2 rounded bg-background/15" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
        {sites.map((site) => {
          const sub = subscriptions.get(site.id);
          const subscription = sub
            ? {
                hasActiveSub: isActiveSub(sub),
                cancelAtPeriodEnd: !!sub.cancelAtPeriodEnd,
              }
            : undefined;
          return (
            <SiteCard
              key={site.id}
              site={site}
              subscription={subscription}
              onDeleted={handleDeleted}
            />
          );
        })}
      </div>
      <Paginator
        pagination={pagination}
        onPageChange={setPage}
        className="mt-8"
      />
    </>
  );
};

export default SiteList;
